import React, { useState, useEffect } from 'react';
import { Modal } from '@/components/ui/Modal/Modal';
import { Input } from '@/components/ui/Input/Input';
import { Select } from '@/components/ui/Select/Select';
import { Button } from '@/components/ui/Button/Button';
import { Checkbox } from '@/components/ui/Checkbox/Checkbox';
import { Textarea } from '@/components/ui/Textarea/Textarea';
import { Spinner } from '@/components/ui/Spinner/Spinner';
import { ChevronDown, Sparkles } from 'lucide-react';
import { ResearchService, type SearchContext } from '@/services/ResearchService';
import { useNotificationStore } from '@/stores/notificationStore';

interface FilterValues {
  min_rooms?: number | null;
  min_conference_capacity?: number | null;
  star_rating?: number | null;
  max_airport_distance_km?: number | null;
  
  // Amenities
  has_spa?: boolean | null;
  min_restaurants?: number | null;
  min_bars?: number | null;
  min_pools?: number | null;
  beach_access?: 'beachfront' | 'beach_access' | 'none' | null;
  all_inclusive?: boolean | null;
  property_type?: 'adults_only' | 'family_friendly' | 'mixed' | null;
  has_golf?: boolean | null;
  min_tripadvisor_rating?: number | null;
}

interface RefineSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  destinationName: string;
  initialFilters?: FilterValues;
  originalFilters?: FilterValues; // Filters from event (for reset functionality)
  onApply: (filters: FilterValues) => void;
  searchContext?: SearchContext | null;
  destinationId?: string;
}

export function RefineSearchModal({
  isOpen,
  onClose,
  destinationName,
  initialFilters = {},
  originalFilters = {},
  onApply,
  searchContext,
  destinationId,
}: RefineSearchModalProps) {
  const [filters, setFilters] = useState<FilterValues>({});
  const [amenitiesExpanded, setAmenitiesExpanded] = useState(false);
  const [aiQuery, setAiQuery] = useState('');
  const [isExtractingCriteria, setIsExtractingCriteria] = useState(false);
  const notificationStore = useNotificationStore();
  const researchService = new ResearchService();

  // Only reset filters when modal opens, not on every initialFilters change
  useEffect(() => {
    if (isOpen) {
      setFilters(initialFilters);
      setAiQuery('');
      // Auto-expand amenities if any amenity filter is set
      const hasAmenityFilters = !!(
        initialFilters.has_spa ||
        initialFilters.min_restaurants ||
        initialFilters.min_bars ||
        initialFilters.min_pools ||
        initialFilters.beach_access ||
        initialFilters.all_inclusive ||
        initialFilters.property_type ||
        initialFilters.has_golf ||
        initialFilters.min_tripadvisor_rating
      );
      setAmenitiesExpanded(hasAmenityFilters);
    }
  }, [isOpen]); // Removed initialFilters from dependencies to prevent infinite loop

  async function handleExtractCriteria() {
    if (!aiQuery.trim() || !searchContext || !destinationId) {
      notificationStore.error('Please provide a search query', {
        title: 'Missing Information',
      });
      return;
    }

    try {
      setIsExtractingCriteria(true);
      
      // Call the lightweight criteria extraction endpoint
      const criteria = await researchService.extractCriteria(
        destinationId,
        aiQuery,
        {
          ...searchContext,
          destination: destinationName,
        }
      );

      // Map SearchCriteria to FilterValues
      const extractedFilters: FilterValues = {
        min_rooms: criteria.min_rooms ?? null,
        min_conference_capacity: criteria.min_conference_capacity ?? null,
        star_rating: criteria.min_star_rating ?? null,
        max_airport_distance_km: criteria.max_airport_distance_km ?? null,
        has_spa: criteria.has_spa ?? null,
        min_restaurants: criteria.min_restaurants ?? null,
        min_bars: criteria.min_bars ?? null,
        min_pools: criteria.min_pools ?? null,
        beach_access: criteria.beach_access ?? null,
        all_inclusive: criteria.all_inclusive ?? null,
        property_type: criteria.property_type ?? null,
        has_golf: criteria.has_golf ?? null,
        min_tripadvisor_rating: criteria.min_tripadvisor_rating ?? null,
      };

      // Update filters with extracted values
      setFilters(extractedFilters);
      
      // Auto-expand amenities if any were extracted
      const hasAmenityFilters = !!(
        extractedFilters.has_spa ||
        extractedFilters.min_restaurants ||
        extractedFilters.min_bars ||
        extractedFilters.min_pools ||
        extractedFilters.beach_access ||
        extractedFilters.all_inclusive ||
        extractedFilters.property_type ||
        extractedFilters.has_golf ||
        extractedFilters.min_tripadvisor_rating
      );
      setAmenitiesExpanded(hasAmenityFilters);

      notificationStore.success('Filters extracted from your description', {
        title: 'Success',
      });
    } catch (error: any) {
      console.error('Failed to extract criteria:', error);
      notificationStore.error(error.message || 'Failed to extract filters from text', {
        title: 'Extraction Failed',
      });
    } finally {
      setIsExtractingCriteria(false);
    }
  }

  function handleReset() {
    // Reset to originalFilters (event filters) if available, otherwise empty
    setFilters(originalFilters || {});
    setAiQuery('');
  }

  function handleApply() {
    onApply(filters);
    onClose();
  }

  function updateFilter<K extends keyof FilterValues>(key: K, value: FilterValues[K]) {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Refine Search - ${destinationName}`}
      size="lg"
    >
      <div className="space-y-6">
        <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
          <p className="text-sm text-gray-700">
            <strong>Searching in:</strong> {destinationName}
          </p>
        </div>

        {/* Search with AI Helper */}
        <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-4 h-4 text-primary-600" />
            <h3 className="text-sm font-medium text-gray-900">Search with AI</h3>
          </div>
          <p className="text-xs text-gray-600 mb-3">
            Describe your requirements and AI will convert them into filter selections
          </p>
          <div className="space-y-2">
            <Textarea
              value={aiQuery}
              onChange={(e) => setAiQuery(e.target.value)}
              placeholder="e.g., luxury hotels with 80+ rooms, conference space for 200 people, near airport, with spa"
              disabled={isExtractingCriteria}
              rows={2}
              className="resize-none"
            />
            <Button
              variant="outline"
              size="sm"
              leftIcon={isExtractingCriteria ? <Spinner size="sm" /> : <Sparkles className="w-4 h-4" />}
              onClick={handleExtractCriteria}
              disabled={!aiQuery.trim() || isExtractingCriteria || !searchContext || !destinationId}
              isLoading={isExtractingCriteria}
            >
              {isExtractingCriteria ? 'Extracting...' : 'Convert to Filters'}
            </Button>
          </div>
        </div>

        {/* Min Rooms */}
        <Input
          label="Minimum Rooms"
          type="number"
          min={0}
          value={filters.min_rooms ?? ''}
          onChange={(e) =>
            updateFilter('min_rooms', e.target.value ? Number(e.target.value) : null)
          }
          placeholder="e.g., 80"
        />

        {/* Conference Capacity & Star Rating */}
        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Min Conference Capacity"
            type="number"
            min={0}
            value={filters.min_conference_capacity ?? ''}
            onChange={(e) =>
              updateFilter('min_conference_capacity', e.target.value ? Number(e.target.value) : null)
            }
            placeholder="e.g., 200"
          />
          <div>
            <label htmlFor="star-rating" className="block text-sm font-medium mb-1">
              Minimum Star Rating
            </label>
            <Select
              id="star-rating"
              value={filters.star_rating?.toString() || ''}
              onChange={(e) =>
                updateFilter('star_rating', e.target.value ? Number(e.target.value) : null)
              }
            >
              <option value="">Any</option>
              <option value="3">3+ Stars</option>
              <option value="4">4+ Stars</option>
              <option value="5">5 Stars</option>
            </Select>
          </div>
        </div>

        {/* Airport Distance */}
        <Input
          label="Max Airport Distance (km)"
          type="number"
          min={0}
          value={filters.max_airport_distance_km ?? ''}
          onChange={(e) =>
            updateFilter('max_airport_distance_km', e.target.value ? Number(e.target.value) : null)
          }
          placeholder="e.g., 30"
        />

        {/* Amenities Section - Collapsible */}
        <div className="border rounded-lg">
          <button
            type="button"
            className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
            onClick={() => setAmenitiesExpanded(!amenitiesExpanded)}
          >
            <h4 className="text-sm font-medium text-gray-900">Amenities</h4>
            <ChevronDown
              className={`w-4 h-4 text-gray-500 transition-transform ${
                amenitiesExpanded ? 'rotate-180' : ''
              }`}
            />
          </button>

          {amenitiesExpanded && (
            <div className="p-4 pt-0 space-y-4 border-t">
              {/* Boolean Amenities - Checkboxes */}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="has_spa"
                    checked={filters.has_spa === true}
                    onChange={(e) => updateFilter('has_spa', e.target.checked ? true : null)}
                  />
                  <label htmlFor="has_spa" className="text-sm text-gray-700 cursor-pointer">
                    Spa Facilities
                  </label>
                </div>

                <div className="flex items-center gap-2">
                  <Checkbox
                    id="all_inclusive"
                    checked={filters.all_inclusive === true}
                    onChange={(e) => updateFilter('all_inclusive', e.target.checked ? true : null)}
                  />
                  <label htmlFor="all_inclusive" className="text-sm text-gray-700 cursor-pointer">
                    All-Inclusive Available
                  </label>
                </div>
              </div>

              {/* Count-Based Amenities */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Minimum Restaurants"
                  type="number"
                  min={0}
                  value={filters.min_restaurants ?? ''}
                  onChange={(e) =>
                    updateFilter('min_restaurants', e.target.value ? Number(e.target.value) : null)
                  }
                  placeholder="e.g., 2"
                />

                <Input
                  label="Minimum Bars/Lounges"
                  type="number"
                  min={0}
                  value={filters.min_bars ?? ''}
                  onChange={(e) =>
                    updateFilter('min_bars', e.target.value ? Number(e.target.value) : null)
                  }
                  placeholder="e.g., 1"
                />

                <Input
                  label="Minimum Pools"
                  type="number"
                  min={0}
                  value={filters.min_pools ?? ''}
                  onChange={(e) =>
                    updateFilter('min_pools', e.target.value ? Number(e.target.value) : null)
                  }
                  placeholder="e.g., 1"
                />

                <div>
                  <label htmlFor="min_tripadvisor_rating" className="block text-sm font-medium text-gray-700 mb-1">
                    Min TripAdvisor Rating
                  </label>
                  <Select
                    id="min_tripadvisor_rating"
                    value={filters.min_tripadvisor_rating?.toString() || ''}
                    onChange={(e) =>
                      updateFilter('min_tripadvisor_rating', e.target.value ? Number(e.target.value) : null)
                    }
                  >
                    <option value="">Any rating</option>
                    <option value="3.0">3.0+ stars</option>
                    <option value="3.5">3.5+ stars</option>
                    <option value="4.0">4.0+ stars</option>
                    <option value="4.5">4.5+ stars</option>
                    <option value="5.0">5.0 stars</option>
                  </Select>
                </div>
              </div>

              {/* Enum-Based Amenities */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="beach_access" className="block text-sm font-medium text-gray-700 mb-1">
                    Beach Access
                  </label>
                  <Select
                    id="beach_access"
                    value={filters.beach_access || ''}
                    onChange={(e) => updateFilter('beach_access', (e.target.value as any) || null)}
                  >
                    <option value="">Any</option>
                    <option value="beachfront">Beachfront</option>
                    <option value="beach_access">Beach Access</option>
                    <option value="none">No Beach Access</option>
                  </Select>
                </div>

                <div>
                  <label htmlFor="property_type" className="block text-sm font-medium text-gray-700 mb-1">
                    Property Type
                  </label>
                  <Select
                    id="property_type"
                    value={filters.property_type || ''}
                    onChange={(e) => updateFilter('property_type', (e.target.value as any) || null)}
                  >
                    <option value="">Any</option>
                    <option value="adults_only">Adults Only</option>
                    <option value="family_friendly">Family Friendly</option>
                    <option value="mixed">Mixed</option>
                  </Select>
                </div>

                <div>
                  <label htmlFor="has_golf" className="block text-sm font-medium text-gray-700 mb-1">
                    Golf Course
                  </label>
                  <Select
                    id="has_golf"
                    value={filters.has_golf === true ? 'yes' : ''}
                    onChange={(e) => updateFilter('has_golf', e.target.value === 'yes' ? true : null)}
                  >
                    <option value="">Any</option>
                    <option value="yes">On-site or Nearby</option>
                  </Select>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-3 pt-4 border-t">
          <Button variant="secondary" onClick={handleReset}>
            Reset Filters
          </Button>
          <Button variant="primary" onClick={handleApply}>
            Apply Filters
          </Button>
        </div>
      </div>
    </Modal>
  );
}

